import cognitoConfig from "@/config/cognito";
import axios from "axios";
// import { redirect } from "react-router";

/**
 * Frontend service for AWS Cognito SSO authentication.
 * Handles OAuth flow, token management, and communication with backend.
 */
class CognitoService {
  private readonly apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "/api";

  /**
   * Generate a random state parameter for CSRF protection.
   */
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  private normalizeHost(host: string): string {
    // supports: "example.com", "example.com:3000", and values coming from req.headers.host
    return (host || "").trim().toLowerCase().replace(/:\d+$/, "");
  }

  /**
   * Build the Cognito authorization URL.
   * This is where users will be redirected to login.
   */
  buildAuthorizationUrl(): string {
    const state = this.generateState();
    const origin = window.location.origin;
    const isWebimax = this.normalizeHost(origin).includes("webimax");


    // Store state in sessionStorage for validation on callback
    sessionStorage.setItem("cognito_oauth_state", state);

    const params = new URLSearchParams({
      response_type: cognitoConfig.responseType,
      client_id: isWebimax ? cognitoConfig.webimaxClientId : cognitoConfig.clientId,
      redirect_uri: cognitoConfig.redirectUri,
      scope: cognitoConfig.scopes.join(" "),
      state: state,
    });

    const identityProvider = isWebimax ? cognitoConfig.webimaxIdentityProvider : cognitoConfig.identityProvider;
    if (identityProvider) {
      params.set("identity_provider", identityProvider);
    }

    return `https://${cognitoConfig.domain}/oauth2/authorize?${params.toString()}`;
  }

  /**
   * Initiate SSO login by redirecting to Cognito Hosted UI.
   */
  login(): void {
    const authUrl = this.buildAuthorizationUrl();
    // console.log("Redirecting to Cognito for authentication:", authUrl);
    window.location.href = authUrl;
  }

  /**
   * Handle OAuth callback after Cognito authentication.
   * Exchanges authorization code for tokens via backend.
   *
   * @param code - Authorization code from URL params
   * @param state - State parameter from URL params
   * @returns User data and tokens
   */
  async handleCallback(
    code: string,
    state: string,
  ): Promise<{
    token: string;
    userData: any;
    cognitoTokens: {
      accessToken: string;
      idToken: string;
      refreshToken: string;
      expiresIn: number;
    };
  }> {
    // Validate state parameter
    const storedState = sessionStorage.getItem("cognito_oauth_state");
    if (state !== storedState) {
      throw new Error("Invalid state parameter - possible CSRF attack");
    }

    // Clear stored state
    sessionStorage.removeItem("cognito_oauth_state");

    try {
      // Exchange code for tokens via backend
      const response = await axios.post(`${this.apiBaseUrl}/auth/cognito/callback`, {
        code,
        state,
        redirectUri: cognitoConfig.redirectUri,
      });

      return response.data;
    } catch (error: any) {
      console.error("Cognito callback error:", error);
      throw new Error(error.response?.data?.error || error.response?.data?.message || "Failed to complete authentication. Please try again.");
    }
  }

  /**
   * Refresh access token using refresh token.
   *
   * @param refreshToken - Cognito refresh token
   * @returns New tokens
   */
  async refreshToken(refreshToken: string): Promise<{
    token: string;
    cognitoTokens: {
      accessToken: string;
      idToken: string;
      expiresIn: number;
    };
  }> {
    try {
      const response = await axios.post(`${this.apiBaseUrl}/auth/cognito/refresh`, {
        refreshToken,
      });

      return response.data;
    } catch (error: any) {
      console.error("Token refresh error:", error);
      throw new Error("Failed to refresh token. Please login again.");
    }
  }

  /**
   * Logout from Cognito and the application.
   *
   * @param logoutUri - Where to redirect after logout (default: login page)
   */
  async logout(): Promise<void> {
    try {
      // Get logout URL from backend
      const response = await axios.post(`${this.apiBaseUrl}/auth/cognito/logout`);

      // Redirect to Cognito logout URL
      window.location.href = response.data.logoutUrl;
    } catch (error) {
      console.error("Logout error:", error);
      // Even if backend call fails, redirect to login
      window.location.href = `/login`;
    }
  }

  /**
   * Verify a Cognito access token.
   *
   * @param accessToken - Cognito access token
   * @returns Verification result
   */
  async verifyToken(accessToken: string): Promise<{ valid: boolean; payload?: any }> {
    try {
      const response = await axios.get(`${this.apiBaseUrl}/auth/cognito/verify`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return response.data;
    } catch (error) {
      return { valid: false };
    }
  }
}

// Export singleton instance
export const cognitoService = new CognitoService();
export default cognitoService;
